import { Link } from "react-router-dom";
import {
  Shield, Terminal, Bug, Server, Activity, Globe, Network,
  ChevronRight, Zap, Lock, BarChart3, Users, Cloud, FileCheck,
  Building2, Clock, CheckCircle2, ArrowRight, Layers, Bot,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const PAIN_POINTS = [
  {
    icon: Layers,
    title: "Too Many Tools, Too Little Time",
    desc: "Juggling Zabbix, LibreNMS, PRTG, vendor portals, and spreadsheets across 10+ clients? We unify everything.",
  },
  {
    icon: Clock,
    title: "Onboarding Takes Weeks",
    desc: "New technicians spend days hunting CLI syntax. Our AI assistant gives instant answers grounded in your actual docs.",
  },
  {
    icon: Bug,
    title: "CVEs Fall Through the Cracks",
    desc: "Critical vulnerabilities affecting your clients' firmware go unnoticed. We surface them automatically from NVD.",
  },
];

const FEATURES = [
  { icon: Bot, title: "AI Network Assistant", desc: "Ask about any CLI command, troubleshooting step, or vendor quirk. Answers grounded in your uploaded docs and 24+ vendor knowledge bases." },
  { icon: Server, title: "Unified Device Inventory", desc: "Auto-discover from 24+ monitoring tools. MAC OUI resolution, interface tracking, and multi-site views in one place." },
  { icon: Bug, title: "CVE Intelligence", desc: "Daily NVD sync flags vulnerabilities affecting your clients' specific vendors and firmware. Never miss a critical patch." },
  { icon: BarChart3, title: "Operational Dashboard", desc: "Device health, CVE trends, sync status, and security posture across all clients at a glance." },
  { icon: Lock, title: "Multi-Tenant RBAC", desc: "Organization-scoped isolation with Owner, Admin, Operator, and Viewer roles. Each client's data stays separate." },
  { icon: FileCheck, title: "Compliance Mapping", desc: "NIST SP 800-53 controls mapped to configs and playbooks. Audit-ready documentation generated automatically." },
];

const INTEGRATIONS = [
  "Zabbix", "LibreNMS", "PRTG", "Nagios", "Datadog", "Prometheus",
  "SolarWinds", "Auvik", "Domotz", "NetBox", "Meraki", "UniFi",
];

const SOCIAL_PROOF = [
  { metric: "24+", label: "Monitoring Integrations" },
  { metric: "30K+", label: "IEEE OUI Vendors" },
  { metric: "250K+", label: "CVEs Tracked" },
  { metric: "800+", label: "NIST Controls" },
];

const USE_CASES = [
  {
    role: "MSP / MSSP",
    desc: "Manage multi-client environments with org-scoped data isolation, unified monitoring, and per-client compliance reporting.",
    icon: Building2,
  },
  {
    role: "Network Consultant",
    desc: "Onboard faster with AI-powered CLI reference, vendor documentation search, and pre-built troubleshooting playbooks.",
    icon: Terminal,
  },
  {
    role: "Enterprise NOC",
    desc: "Correlate CVEs to your inventory, track config drift, and maintain audit-ready compliance documentation across your infrastructure.",
    icon: Shield,
  },
];

const Landing = () => (
  <div className="min-h-screen bg-background">
    {/* Nav */}
    <nav className="border-b border-border px-4 sm:px-6 py-3 sm:py-4 sticky top-0 bg-background/95 backdrop-blur z-50">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="rounded-lg bg-primary/10 p-1.5 sm:p-2 glow-cyan">
            <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          </div>
          <span className="text-base sm:text-lg font-bold tracking-tight text-foreground">NetOps Security</span>
        </div>
        <div className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
          <a href="#pain" className="hover:text-foreground transition-colors">Why Us</a>
          <a href="#features" className="hover:text-foreground transition-colors">Features</a>
          <a href="#use-cases" className="hover:text-foreground transition-colors">Use Cases</a>
          <Link to="/pricing" className="hover:text-foreground transition-colors">Pricing</Link>
          <Link to="/api-docs" className="hover:text-foreground transition-colors">API</Link>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/auth">
            <Button variant="ghost" size="sm" className="text-xs sm:text-sm">Sign In</Button>
          </Link>
          <Link to="/auth">
            <Button size="sm" className="text-xs sm:text-sm">Start Free</Button>
          </Link>
        </div>
      </div>
    </nav>

    <main>
    {/* Hero */}
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-14 sm:pb-20 text-center relative z-10">
        <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 sm:px-4 py-1.5 mb-4 sm:mb-6">
          <Zap className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-primary" />
          <span className="text-[10px] sm:text-xs font-medium text-primary">Built for MSPs & Network Consultants</span>
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold tracking-tight text-foreground leading-tight max-w-4xl mx-auto">
          Stop Juggling Tools.{" "}
          <span className="text-primary text-glow">Start Scaling.</span>
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          One AI-powered platform to manage multi-client networks — inventory, vulnerabilities,
          compliance, and troubleshooting — so your team can handle 10x more clients without 10x more headcount.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/auth">
            <Button size="lg" className="gap-2 px-8">
              Start Free Trial <ChevronRight className="h-4 w-4" />
            </Button>
          </Link>
          <Link to="/demo">
            <Button size="lg" variant="outline" className="gap-2 px-8">
              <Activity className="h-4 w-4" /> Try Live Demo
            </Button>
          </Link>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">No credit card required · Set up in 5 minutes · Cancel anytime</p>

        {/* Stats */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
          {SOCIAL_PROOF.map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-3xl font-bold font-mono text-primary">{s.metric}</p>
              <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Pain Points */}
    <section id="pain" className="border-t border-border bg-card/30">
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">Sound Familiar?</h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            The problems every MSP and consultant faces — and how we solve them.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {PAIN_POINTS.map((p) => (
            <div key={p.title} className="text-center space-y-4">
              <div className="mx-auto rounded-xl bg-destructive/10 p-4 w-fit">
                <p.icon className="h-7 w-7 text-destructive" />
              </div>
              <h3 className="font-semibold text-foreground text-lg">{p.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Features */}
    <section id="features" className="max-w-6xl mx-auto px-6 py-20">
      <div className="text-center mb-14">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground">Everything You Need in One Platform</h2>
        <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
          Replace your patchwork of tools with a single, AI-native operations hub.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {FEATURES.map((f) => (
          <Card key={f.title} className="bg-card border-border hover:border-primary/30 transition-colors group">
            <CardContent className="p-6">
              <div className="rounded-lg bg-primary/10 p-2.5 w-fit mb-4 group-hover:bg-primary/20 transition-colors">
                <f.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>

    {/* Use Cases */}
    <section id="use-cases" className="border-t border-border bg-card/30">
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">Built For Your Workflow</h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            Whether you manage 5 clients or 500 sites, NetOps Security scales with you.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {USE_CASES.map((uc) => (
            <Card key={uc.role} className="bg-card border-border">
              <CardContent className="p-8 text-center space-y-4">
                <div className="mx-auto rounded-xl bg-primary/10 p-4 w-fit">
                  <uc.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-bold text-foreground text-lg">{uc.role}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{uc.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>

    {/* Multi-Tenant Isolation */}
    <section className="border-t border-border bg-card/30">
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 mb-4">
              <Lock className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-medium text-primary">Enterprise Security</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              True Multi-Tenant Isolation
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Every client gets their own organization with <strong className="text-foreground">Row-Level Security</strong> enforced
              at the database level. Your clients' data never co-mingles — even if you manage 500 tenants from one account.
            </p>
            <ul className="space-y-3">
              {[
                "Separate org per client with unique data scope",
                "RBAC: Owner, Admin, Operator, Viewer roles",
                "Invite team members with role-based email invites",
                "Full audit log per organization",
                "Compliance reports scoped per tenant",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-border bg-card p-6 space-y-3">
            <p className="text-xs text-muted-foreground font-mono mb-2">org_members (Row-Level Security)</p>
            {[
              { org: "Acme Corp", role: "Admin", users: 4 },
              { org: "TechStart Inc", role: "Operator", users: 2 },
              { org: "MegaRetail", role: "Owner", users: 8 },
            ].map((row) => (
              <div key={row.org} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/50">
                <div className="flex items-center gap-3">
                  <Building2 className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">{row.org}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className="text-xs">{row.role}</Badge>
                  <span className="text-xs text-muted-foreground">{row.users} users</span>
                </div>
              </div>
            ))}
            <p className="text-xs text-muted-foreground text-center pt-2">
              Each org sees <strong>only their own data</strong> — enforced at every query
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Integrations */}
    <section id="integrations" className="max-w-6xl mx-auto px-6 py-20">
      <div className="text-center mb-10">
        <h2 className="text-2xl font-bold text-foreground mb-3">Connects to Your Existing Stack</h2>
        <p className="text-muted-foreground max-w-lg mx-auto">
          Import devices from the tools you already use. One-click setup, automatic hourly syncs.
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
        {INTEGRATIONS.map((name) => (
          <span key={name} className="px-4 py-2 rounded-full border border-border bg-card text-sm text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors">
            {name}
          </span>
        ))}
        <span className="px-4 py-2 rounded-full border border-primary/30 bg-primary/5 text-sm text-primary font-medium">
          +12 more
        </span>
      </div>
    </section>

    {/* How It Works */}
    <section className="border-t border-border bg-card/30">
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">Up and Running in 5 Minutes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {[
            { step: "1", title: "Create Your Org", desc: "Sign up, name your organization, and invite your team with role-based access." },
            { step: "2", title: "Connect Monitoring", desc: "Add your Zabbix, LibreNMS, or any of 24+ tools. Devices sync automatically." },
            { step: "3", title: "Ask the AI", desc: "Search CLI commands, check CVEs, upload vendor docs — the AI learns your environment." },
          ].map((s) => (
            <div key={s.step} className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-xl font-bold text-primary font-mono">{s.step}</span>
              </div>
              <h3 className="font-semibold text-foreground">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="max-w-6xl mx-auto px-6 py-20 text-center">
      <div className="rounded-2xl border border-border bg-card p-12 md:p-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
        <div className="relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Ready to 10x Your Efficiency?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-lg mx-auto text-lg">
            Join MSPs and consultants who manage more clients with less chaos.
            Free to start — no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/auth">
              <Button size="lg" className="gap-2 px-8">
                Start Free Trial <ChevronRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> 5-minute setup</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> Multi-tenant from day 1</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> 24+ integrations</span>
          </div>
        </div>
      </div>
    </section>

    {/* Footer */}
    </main>

    <footer className="border-t border-border px-6 py-8">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-primary" />
          <span className="text-sm text-muted-foreground">NetOps Security © {new Date().getFullYear()}</span>
        </div>
        <div className="flex items-center gap-6 text-sm text-muted-foreground">
          <span className="hover:text-foreground cursor-pointer transition-colors">Privacy</span>
          <span className="hover:text-foreground cursor-pointer transition-colors">Terms</span>
          <span className="hover:text-foreground cursor-pointer transition-colors">Documentation</span>
        </div>
      </div>
    </footer>
  </div>
);

export default Landing;
